import React, { useState } from 'react'
import OperationWorkflowModal from 'common/components/OperationWorkflowModal'
import API from 'common/logic/API'
import { Button, Col, Form } from 'react-bootstrap'
import { Formik } from 'formik'
import * as yup from 'yup'
import * as Types from 'common/utils/Types'
import { Navigate } from 'react-router-dom'

interface Props {
    onSuccess: string | ((result: unknown) => void)
    static?: boolean
    entryId?: number
}

function EditFundWorkflow(props: Props) {

    const [redirect, setRedirect] = useState<string | null>(null)

    if (redirect) {
        return <Navigate to={redirect} replace />
    }

    const loadingStep = (_parameters: unknown) => {
        const dataToFetch: Record<string, any> = {
            "currencies": API.get('/currencies'),
            "institutions": API.get('/institutions')
        }
        if (props.entryId !== undefined) {
            dataToFetch.entry = API.get(`/funds/${props.entryId}`)
        }
        return API.multiple(dataToFetch)
            .then((r: any) => ({
                currencies: r.currencies.results,
                entry: r["entry"],
                institutions: r.institutions.results
            }))
    }

    const userInputStep = (_parameters: unknown, loading_result: unknown, _values: unknown, _errors: unknown, completion: (input: unknown) => void) => {
        const lr = loading_result as any
        const formEl = form(lr.currencies, lr.entry, lr.institutions, completion)
        return { component: formEl, title: props.entryId ? "Edit fund" : "New fund" }
    }

    const executionStep = (_parameters: unknown, _loading_result: unknown, user_input: unknown) => {
        if (props.entryId) {
            return API.put(`/funds/${props.entryId}`, user_input)
        } else {
            return API.post("/funds", user_input)
        }
    }

    const completionCallback = (execution_result: unknown) => {
        if (Types.isString(props.onSuccess)) {
            setRedirect(props.onSuccess as string)
        } else {
            (props.onSuccess as (result: unknown) => void)(execution_result && (execution_result as any).data)
        }
    }

    function form(loaded_currencies: any[], previousValues: any, institutions: string[], completion: (input: unknown) => void) {

        const parseForm = (values: any) => {
            const data: Record<string, unknown> = {
                currencyId: values.currency,
                name: values.name,
                amount: values.amount,
                usedForNewMovements: values.usedForNewMovements,
                isInvestment: values.isInvestment,
                deleted: false,
                shortName: values.shortName
            }
            if (values.institution !== "") {
                data.institution = values.institution
            }
            completion(data)
        }

        const schema = yup.object().shape({
            currency: yup.number(),
            name: yup.string().required(),
            amount: yup.number().required(),
            usedForNewMovements: yup.boolean(),
            isInvestment: yup.boolean(),
            shortName: yup.string().required(),
            institution: yup.string()
        })

        const currencies = loaded_currencies.map((item: any) => <option value={item.id} key={`cat${item.id}`}>{item.name}</option>)

        const initialValues = previousValues ? {
            currency: previousValues.currency.id,
            name: previousValues.name,
            amount: previousValues.amount,
            usedForNewMovements: previousValues.usedForNewMovements ?? true,
            isInvestment: previousValues.isInvestment ?? false,
            shortName: previousValues.shortName,
            institution: previousValues.institution ?? ""
        } : {
            currency: loaded_currencies[0].id,
            name: "",
            amount: "",
            usedForNewMovements: true,
            isInvestment: false,
            shortName: "",
            institution: ""
        }

        const institutionsOptions = institutions.map((item: string) => <option value={item} key={`inst_${item}`}>{item}</option>)

        return <>
            <Formik
                validationSchema={schema}
                onSubmit={parseForm}
                initialValues={initialValues}
            >
                {({
                    handleSubmit,
                    handleChange,
                    values,
                    errors,
                }) => (
                    <Form noValidate onSubmit={handleSubmit}>

                        <Form.Row>
                            <Form.Group as={Col} controlId="formName">
                                <Form.Label>Name</Form.Label>
                                <Form.Control
                                    type='text'
                                    name='name'
                                    placeholder="Name"
                                    value={values.name}
                                    onChange={handleChange}
                                    isInvalid={!!errors.name}
                                />
                                <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group as={Col} controlId="formShortName">
                                <Form.Label>Short name</Form.Label>
                                <Form.Control
                                    type='text'
                                    name='shortName'
                                    placeholder="Short name"
                                    value={values.shortName}
                                    onChange={handleChange}
                                    isInvalid={!!errors.shortName}
                                />
                                <Form.Control.Feedback type="invalid">{errors.shortName}</Form.Control.Feedback>
                            </Form.Group>
                        </Form.Row>

                        <Form.Row>
                            <Form.Group as={Col} controlId="formAmount">
                                <Form.Label>Amount</Form.Label>
                                <Form.Control
                                    type='text'
                                    name='amount'
                                    placeholder="1000.00"
                                    value={values.amount}
                                    onChange={handleChange}
                                    isInvalid={!!errors.amount}
                                />
                                <Form.Control.Feedback type="invalid">{errors.amount}</Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group as={Col} controlId="formCurrency">
                                <Form.Label>Currency</Form.Label>
                                <Form.Control
                                    as='select'
                                    name='currency'
                                    value={values.currency}
                                    onChange={handleChange}
                                    isInvalid={!!errors.currency}
                                >
                                    {currencies}
                                </Form.Control>
                                <Form.Control.Feedback type="invalid">{errors.currency}</Form.Control.Feedback>
                            </Form.Group>
                        </Form.Row>

                        <Form.Group controlId="formUsedForNewMovements">
                            <Form.Check
                                type='checkbox'
                                label='Use for new movements'
                                name='usedForNewMovements'
                                checked={values.usedForNewMovements}
                                onChange={handleChange}
                            />
                        </Form.Group>
                        <Form.Group controlId="formIsInvestment">
                            <Form.Check
                                type='checkbox'
                                label='Investment fund'
                                name='isInvestment'
                                checked={values.isInvestment}
                                onChange={handleChange}
                            />
                        </Form.Group>

                        <Form.Group controlId="formInstitution">
                            <Form.Label>Institution</Form.Label>
                            <Form.Control
                                as='select'
                                name='institution'
                                value={values.institution}
                                onChange={handleChange}
                                isInvalid={!!errors.institution}
                            >
                                <option></option>
                                {institutionsOptions}
                            </Form.Control>
                            <Form.Control.Feedback type="invalid">{errors.institution}</Form.Control.Feedback>
                        </Form.Group>

                        <Button variant="primary" type="submit">Save</Button>

                    </Form>
                )}
            </Formik>
        </>
    }

    return <OperationWorkflowModal
        static={props.static}
        loadingStep={loadingStep}
        userInputStep={userInputStep}
        executionStep={executionStep}
        completionCallback={completionCallback}
    />
}

export default EditFundWorkflow
